import { NetworkError, UnreachableError } from "./request.helpers";

export type ErrorKind = "unreachable" | "forbidden" | "notFound" | "other";

// The Reason the sales demo answers a change it does not support with
// (src/demo/transport.ts). The global mutation handler shows a friendly notice
// for it instead of an error.
export const DEMO_BLOCKED_REASON = "not available in demo";

const GATEWAY_STATUS_CODES = new Set([502, 503, 504]);

export function isGatewayStatus(status: number): boolean {
  return GATEWAY_STATUS_CODES.has(status);
}

export function isUnreachable(error: unknown): boolean {
  return (
    error instanceof UnreachableError ||
    (error instanceof NetworkError && isGatewayStatus(error.StatusCode))
  );
}

// fetch() also rejects after a request was sent: a connection reset, a backend
// restart mid-request, or a CORS-blocked response. This app's requests are
// CORS-simple, so the server has already processed them. So "not saved" is
// claimed only when the browser was offline at send time. A 502/503/504 or any
// other rejection may hide a committed write, so the user is asked to check.
// A GET carries no changes, so it gets the neutral retry text, which is the
// same key ErrorComponent uses. That holds whether fetch() rejected or a proxy
// answered 502/503/504, so NetworkError records its request method too.
// meta.savesNothing marks a mutation that changes no saved data (sign-in, 2FA).
// It is checked first, so even an offline-at-send sign-in gets the neutral text
// instead of "your changes were not saved".
// Reference: offline audit 2026-10-02 finding 10, amends WR-05; offline audit
// item 13 b-c.
export function unreachableMutationMessageKey(
  error: unknown,
  meta?: Readonly<Record<string, unknown>>,
):
  | "common.serverUnreachable.changesNotSaved"
  | "common.serverUnreachable.changesMayNotBeSaved"
  | "common.queryError.unreachable.description" {
  if (meta?.savesNothing === true) {
    return "common.queryError.unreachable.description";
  }
  if (
    (error instanceof UnreachableError || error instanceof NetworkError) &&
    error.method === "GET"
  ) {
    return "common.queryError.unreachable.description";
  }
  if (error instanceof UnreachableError && error.offlineAtSend) {
    return "common.serverUnreachable.changesNotSaved";
  }
  return "common.serverUnreachable.changesMayNotBeSaved";
}

export function classifyError(error: unknown): ErrorKind {
  if (isUnreachable(error)) return "unreachable";
  if (error instanceof NetworkError) {
    if (error.StatusCode === 403) return "forbidden";
    if (error.StatusCode === 404) return "notFound";
  }
  return "other";
}

export function shouldRetryQuery(
  failureCount: number,
  error: unknown,
): boolean {
  // Order matters: 502-504 are 5xx, so the unreachable check runs first and
  // never retries them (ServerRecovery owns recovery). Then all 4xx (incl. 401).
  if (isUnreachable(error)) return false;
  if (
    error instanceof NetworkError &&
    error.StatusCode >= 400 &&
    error.StatusCode < 500
  ) {
    return false;
  }
  return failureCount < 3;
}
